/**
 * AdminHome.tsx — /admin, the consolidated "Admin Tools" landing page. Replaces what used to be
 * FOUR separate ROLE_GATED_NAV entries (Administration, Review queue, Moderation queue, Support &
 * issues) with one nav entry that opens here; the underlying /review, /moderation, /support-queue
 * routes are UNCHANGED and still directly reachable, just no longer individually nav-listed. See
 * REQUIREMENTS_PROJECT_CONTEXT.md §10 group C and ALIGNMENT_CHANGELOG.md's entry closing gap G-07.
 *
 * Role-scoped entries, each with a live count from GET /api/admin/summary (PC-F16's "needs attention"
 * summary) — the count is simply omitted, not shown as zero, for anything the viewer's roles don't
 * unlock (the backend never sends that key at all). PlatformOwner additionally sees the tool-governance
 * count and the stated-missing "neglected tools" line.
 *
 * A summary that could not be READ is a third state, not an empty one: see `AdminSummaryState`. No
 * figure on this page is ever coalesced to zero to paper over a failed request.
 *
 * ── This surface was migrated onto the teal design system (the "Upload a tool" look) ──────────────
 * The landing page is now a tidy BentoGrid of tiles, and EVERY governance/queue/insight destination
 * opens IN PLACE as a centred overlay over this grid (the model the Profile "Publish" button already
 * uses for ShareOverlay) instead of navigating to a separate page — Russell's "open an overlay
 * instead of a new tab". Originally only the six governance panels were in this pass; the three
 * moderation QUEUES (Review/Moderation/Support) and the learning-metrics report were later brought in
 * too (Mauro, 2026-08-27: "I want it to be the same pop up like the other governance ones"), so they
 * are now overlay panels as well rather than plain navigations.
 *
 * Every sub-route stays registered (deep links, bookmarks): each sub-page renders the SAME panel
 * component this page mounts, just always-open over a blank backdrop. Each panel fetches its own data
 * on open (useOverlayData), so this loader is unchanged and preloads nothing for them.
 */
import { lazy, Suspense, useRef, useState, type ReactNode } from 'react';
import { useLoaderData } from 'react-router';
import { motion } from 'motion/react';
import type { AppointedRole } from '@trailblazer/shared';
import { apiGet } from './api';
import { SignedOut } from './shared';
import { ProvisionalNote } from './overlay';
import { staggerContainer, riseInItem } from '../tools/upload/design/motion';
import { BentoGrid } from '../tools/upload/design/BentoGrid';
// The ten panels opened in place over this grid — six governance, three queues, one insight report.
// They live beside their loaders in their own files; AdminHome imports only the PANEL (the overlay
// body), never the route wrapper.
import { AppointmentsPanel } from './Appointments';
import { TeamsPanel } from './Teams';
import { ToolGovernancePanel } from './ToolGovernance';
import { AccessRequirementsPanel } from './AccessRequirements';
import { PlatformSettingsPanel } from './PlatformSettings';
import { HeadcountPanel } from './Headcount';
import { TermsOfUsePanel } from './TermsOfUse';
// The three queues + the learning-metrics report — brought into the overlay model on 2026-08-27
// (previously plain navigations). Same panel contract as the governance six.
import { ReviewQueuePanel } from './ReviewQueue';
import { ModerationQueuePanel } from './ModerationQueue';
import { SupportQueuePanel } from './SupportQueue';
import { LearningMetricsPanel } from './LearningMetrics';
import './adminHome.css';
/**
 * The animated Topography ripple, CODE-SPLIT out of the main bundle exactly as the /me profile header
 * splits it (Profile.tsx): it pulls in `ogl` (a WebGL library, most of a ~250kB chunk), so `lazy` keeps
 * it off every route that has no band. Safe to defer because the band is purely decorative and takes no
 * layout space — it is `position:absolute; inset:0; pointer-events:none` inside the band (.admin-band__topo),
 * so the `null` Suspense fallback renders nothing, shifts nothing, and blocks no interaction. */
const Topography = lazy(() => import('../design/Topography'));

/**
 * Mirrors db/adminSummary.ts's AdminSummary — every field OPTIONAL, meaning "not yours to see".
 *
 * ABSENCE HAS EXACTLY ONE MEANING HERE, and that is why `AdminSummaryState` below exists: a field is
 * missing only because the viewer's appointments do not unlock it. A summary that could not be READ
 * is not an object with missing fields, it is a different state entirely.
 */
interface AdminSummary {
  pendingSubmissions?: number;
  flaggedContent?: number;
  unresolvedSupportRequests?: number;
  brokenOrOwnerlessTools?: number;
  /** A number, or 'unavailable' when view tracking has not covered the window yet — not the same as 0. */
  neglectedTools?: number | 'unavailable';
}

/**
 * The needs-attention counts, or the reason there are none to show — the same
 * unavailable-carrying-a-human-reason shape the data layer uses for a suppressed aggregate
 * (db/managerMetrics.ts's Unavailable, dashboard/api.ts's CohortCount), applied to a failed read.
 *
 * This used to be a bare `AdminSummary`, with ANY non-OK response — a 500, a 403, a proxy eating the
 * request mid-deploy — collapsed to `{}` and then rendered through `?? 0`. The platform owner was
 * shown "0 broken or ownerless tools", which is the most reassuring possible reading of no data and
 * the one figure on the page they would act on. A figure that cannot be computed says so.
 */
export type AdminSummaryState =
  { kind: 'ok'; summary: AdminSummary } | { kind: 'unavailable'; reason: string };

export type AdminHomeLoaderData =
  { state: 'signedOut' } | { state: 'ok'; roles: AppointedRole[]; summary: AdminSummaryState };

/**
 * Reads the caller's own live roles from GET /api/me (the same field App.tsx's useAuth() already
 * reads to decide which ROLE_GATED_NAV entries to show) and the needs-attention summary, in
 * parallel. A viewer who sees "Admin Tools" in the nav always resolves to at least one section
 * here (never an empty page for a genuine holder) — /api/admin/summary shares the exact same
 * any-of role gate as the nav entry, so the two can never disagree.
 *
 * Both reads go through admin/api.ts's wrappers, like every other screen in this folder. They were
 * hand-rolled `fetch` calls until this pass, which meant '/api/me' and '/api/admin/summary' each
 * existed in two places with two different notions of what a 401 or a malformed body means, and only
 * the wrapper's notion was ever tested (test/communityApi.test.ts). One normalised result union is
 * also what makes the reason strings below possible: a raw Response cannot tell you the `error` code.
 */
export async function adminHomeLoader(): Promise<AdminHomeLoaderData> {
  const [meRes, summaryRes] = await Promise.all([
    apiGet<{ roles?: AppointedRole[] }>('/api/me'),
    apiGet<AdminSummary>('/api/admin/summary'),
  ]);
  if (meRes.kind === 'unauthorized') return { state: 'signedOut' };
  if (meRes.kind === 'error') throw new Error(`GET /api/me failed (${meRes.status})`);
  const roles = meRes.body.roles ?? [];

  // A 403 is the one non-OK answer that is not a fault: the caller holds none of the four roles the
  // summary covers, so the page renders its "nothing unlocked" state. It is still reported as
  // unavailable rather than as zeroes, because "you may not see these counts" and "these counts are
  // zero" are different statements and only one of them is true.
  const summary: AdminSummaryState =
    summaryRes.kind === 'ok'
      ? { kind: 'ok', summary: summaryRes.body ?? {} }
      : {
          kind: 'unavailable',
          reason:
            summaryRes.kind === 'unauthorized'
              ? 'your session ended while this page was loading'
              : summaryRes.status === 403
                ? 'none of your current appointments unlock these counts'
                : `the summary request failed (${summaryRes.status}${summaryRes.code ? `: ${summaryRes.code}` : ''})`,
        };

  return { state: 'ok', roles, summary };
}

/** Which panel is open over the grid (null = none) — governance, queue and insight alike. */
type AdminPanel =
  | 'appointments'
  | 'teams'
  | 'tools'
  | 'access'
  | 'settings'
  | 'headcount'
  | 'terms'
  | 'review'
  | 'moderation'
  | 'support'
  | 'metrics';

// The tile geometry, lifted VERBATIM from BentoGridItem so a tile and an upload tool card share one
// look — but rendered on the interactive element ITSELF (a motion.a / motion.button) rather than
// wrapping BentoGridItem in a link. Two reasons: (1) a stagger cascades only to its DIRECT motion
// children, so the animated element must be the grid's own child, not a plain <a> around it; (2) one
// motion element means one rise-in, not the doubled entrance a motion-inside-a-link would play. The
// `group/bento` hover + `riseInItem` entrance are the same ones BentoGridItem carries.
// `border-control` (0.3), not `border-subtle` (0.2): the tiles must read as buttons AT REST, before any
// hover — a 0.2 hairline was so faint the outline only registered once the hover shadow lit it up. The
// hover shadow stays as the extra affordance; the resting border now carries the "this is clickable" cue.
const TILE_CLASS = [
  'group/bento flex w-full flex-col justify-between gap-4 rounded-md border border-control bg-surface p-card text-left',
  'transition-shadow duration-micro ease-out hover:shadow-[0px_2px_3px_2px_var(--accent-subtle)]',
  'outline-none focus-visible:border-ring-focus',
].join(' ');

// Inside the 70% main column the auto-fill `grid-cols-cards` (191px min) would pack four or five
// narrow tiles per row and re-cram the page — the "smush of options" this redesign is undoing. A
// fixed responsive count lets the tiles breathe: one column on a phone, two at the `stack` (645px)
// break, three once the sidebar splits off at `sidebar` (1024px). Passed to BentoGrid, whose `cn`
// (tailwind-merge) treats grid-cols-* as one group, so these override its base `grid-cols-cards`.
const SECTION_GRID = 'grid-cols-1 stack:grid-cols-2 sidebar:grid-cols-3';

/** The inner content of a tile — icon, title (+ optional count), hint — with the hover nudge. */
function TileBody({
  icon,
  label,
  hint,
  count,
}: {
  icon: ReactNode;
  label: string;
  hint: string;
  count?: number | undefined;
}) {
  return (
    <div className="transition-transform duration-micro ease-out group-hover/bento:translate-x-1">
      <span className="text-accent">{icon}</span>
      <div className="mt-2 mb-1 flex items-center gap-3 font-sans text-base font-medium text-text">
        {label}
        {/* The live count, when the summary carried one. A count of undefined shows NOTHING (the
            load-bearing "omitted, not zero" rule); a real 0 shows as 0. Plain chip, not <Pill>,
            which hardcodes a 32px/13px size that fights a count badge. */}
        {count !== undefined && (
          <span className="inline-flex h-[22px] items-center rounded-sm bg-accent-subtle px-2 text-sm font-light text-text">
            {count}
          </span>
        )}
      </div>
      <div className="font-sans text-sm font-light text-text-inactive">{hint}</div>
    </div>
  );
}

/**
 * One clickable tile in a grid section. Every admin tile is now a button that opens its panel in
 * place over the grid (queues, insight and governance alike). The link arm is retained as a general
 * capability for any future tile that should navigate rather than open in place. Both arms render as
 * direct motion children of the section's stagger container so the entrance stagger reaches them.
 */
function TileShell(
  props: {
    icon: ReactNode;
    label: string;
    hint: string;
    count?: number | undefined;
  } & (
    | { as: 'link'; href: string }
    | { as: 'button'; onOpen: () => void; triggerRef: React.RefObject<HTMLButtonElement> }
  ),
) {
  const { icon, label, hint, count } = props;
  const inner = <TileBody icon={icon} label={label} hint={hint} count={count} />;

  if (props.as === 'link') {
    return (
      <motion.a variants={riseInItem} href={props.href} className={TILE_CLASS}>
        {inner}
      </motion.a>
    );
  }
  return (
    <motion.button
      ref={props.triggerRef}
      variants={riseInItem}
      type="button"
      onClick={props.onOpen}
      className={TILE_CLASS}
    >
      {inner}
    </motion.button>
  );
}

/**
 * One row in the at-a-glance rail: a queue label and its live pending count, the whole row a button
 * that opens the same panel the matching tile does. The count follows the same rule as the tile badge
 * — an undefined count shows NOTHING (omitted, not zero), a real 0 shows as 0.
 */
function GlanceRow({
  label,
  count,
  onOpen,
}: {
  label: string;
  count?: number | undefined;
  onOpen: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center justify-between gap-3 rounded-sm border border-control bg-surface px-2 py-2 text-left outline-none transition-colors duration-micro ease-out hover:bg-accent-subtle focus-visible:border-ring-focus"
      >
        <span className="min-w-0 break-words font-sans text-sm font-light text-text">{label}</span>
        {count !== undefined && (
          <span className="inline-flex h-[22px] shrink-0 items-center rounded-sm bg-accent-subtle px-2 text-sm font-light text-text">
            {count}
          </span>
        )}
      </button>
    </li>
  );
}

/** A needs-attention figure, rendered as a teal stat plate. Preserves the three-absence logic. */
function StatPlate({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-md border border-subtle bg-surface p-card">
      <div className="text-sm font-light text-text-inactive">{label}</div>
      <div className="mt-2 text-2xl font-light text-text">{children}</div>
    </div>
  );
}

// ── Tile icons — simple line glyphs in currentColor, sized to sit on the accent. Kept inline (these
// six are admin-specific and not in the shared upload icon set), all 20px, stroke-based to read at
// tile scale. ─────────────────────────────────────────────────────────────────────────────────────
const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};
function I({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}
const ICONS = {
  review: (
    <I>
      <path d="M4 5h16M4 12h16M4 19h10" {...stroke} />
    </I>
  ),
  moderation: (
    <I>
      <path d="M12 3l7 4v5c0 4-3 7-7 9-4-2-7-5-7-9V7l7-4z" {...stroke} />
    </I>
  ),
  support: (
    <I>
      <circle cx="12" cy="12" r="9" {...stroke} />
      <path d="M12 8v5M12 16h.01" {...stroke} />
    </I>
  ),
  metrics: (
    <I>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" {...stroke} />
    </I>
  ),
  appointments: (
    <I>
      <circle cx="9" cy="8" r="3" {...stroke} />
      <path d="M3 20c0-3 3-5 6-5s6 2 6 5M17 8h5M19.5 5.5v5" {...stroke} />
    </I>
  ),
  teams: (
    <I>
      <circle cx="12" cy="6" r="2.5" {...stroke} />
      <circle cx="5" cy="16" r="2.5" {...stroke} />
      <circle cx="19" cy="16" r="2.5" {...stroke} />
      <path d="M12 8.5v3M12 11.5l-6 2.2M12 11.5l6 2.2" {...stroke} />
    </I>
  ),
  tools: (
    <I>
      <path d="M14 6l4 4-8 8-4 1 1-4 8-8zM12 8l4 4" {...stroke} />
    </I>
  ),
  access: (
    <I>
      <rect x="5" y="10" width="14" height="10" rx="2" {...stroke} />
      <path d="M8 10V7a4 4 0 018 0v3" {...stroke} />
    </I>
  ),
  settings: (
    <I>
      <circle cx="12" cy="12" r="3" {...stroke} />
      <path
        d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"
        {...stroke}
      />
    </I>
  ),
  headcount: (
    <I>
      <circle cx="8" cy="8" r="3" {...stroke} />
      <circle cx="17" cy="9" r="2.5" {...stroke} />
      <path d="M2 20c0-3 2.5-5 6-5s6 2 6 5M15 15c3 0 5 2 5 5" {...stroke} />
    </I>
  ),
  terms: (
    <I>
      <path d="M7 3h7l4 4v14H7zM14 3v4h4M9 12h7M9 16h7" {...stroke} />
    </I>
  ),
};

// The role named on the band's "signed in as" line — the SINGLE most senior appointment the viewer
// holds, most-senior first so `.find` picks it. Labels are the human-facing names for the appointment
// enum (ChampionRole reads "Champion"). Names the appointment, never a person (PC-F31).
const PRIMARY_ROLE_ORDER: { role: AppointedRole; label: string }[] = [
  { role: 'PlatformOwner', label: 'Platform Owner' },
  { role: 'SWATMember', label: 'AI SWAT' },
  { role: 'Reviewer', label: 'Reviewer' },
  { role: 'Manager', label: 'Manager' },
  { role: 'ChampionRole', label: 'Champion' },
];

export function AdminHome() {
  const data = useLoaderData() as AdminHomeLoaderData;
  const [openPanel, setOpenPanel] = useState<AdminPanel | null>(null);
  // Each governance tile keeps a ref so focus returns to the tile that opened a panel when it closes
  // (the same returnFocusRef contract the Profile panels use). useFocusTrap already restores focus to
  // whatever was active on open, so these are the natural landing spots.
  const triggerRefs = {
    appointments: useRef<HTMLButtonElement>(null),
    teams: useRef<HTMLButtonElement>(null),
    tools: useRef<HTMLButtonElement>(null),
    access: useRef<HTMLButtonElement>(null),
    settings: useRef<HTMLButtonElement>(null),
    headcount: useRef<HTMLButtonElement>(null),
    terms: useRef<HTMLButtonElement>(null),
    review: useRef<HTMLButtonElement>(null),
    moderation: useRef<HTMLButtonElement>(null),
    support: useRef<HTMLButtonElement>(null),
    metrics: useRef<HTMLButtonElement>(null),
  };

  if (data.state === 'signedOut') {
    return <SignedOut title="Admin Tools" returnTo="/admin" />;
  }

  const { roles, summary } = data;
  // The counts, or null when the summary could not be read. NEVER coalesced to zero, and no count is
  // rendered at all in that case: an omitted number reads as "nothing to tell you here", whereas a
  // printed 0 reads as "checked, and there is nothing wrong". The notice below carries the reason so
  // the absence is legible as a failure rather than as calm.
  const counts = summary.kind === 'ok' ? summary.summary : null;
  const unavailableReason = summary.kind === 'unavailable' ? summary.reason : null;
  const isReviewer = roles.includes('Reviewer');
  const isChampion = roles.includes('ChampionRole');
  const isSwat = roles.includes('SWATMember');
  const isManager = roles.includes('Manager');
  const isOwner = roles.includes('PlatformOwner');

  const close = () => setOpenPanel(null);

  // The one role to name on the band's "signed in as" line — the highest the viewer holds, in the same
  // seniority order the sections below use (Owner outranks the rest). Aggregate only; names the
  // appointment, never a person.
  const primaryRole = PRIMARY_ROLE_ORDER.find((r) => roles.includes(r.role));

  return (
    <section
      aria-labelledby="admin-heading"
      className="admin-surface admin-page mx-auto max-w-frame font-sans"
    >
      {/* Hero band — the charcoal sibling of the /me profile header, carrying the SAME animated
          Topography ripple recoloured grey (see adminHome.css). Full-bleed, flush under the app
          header. The heading stays the labelled <h1> for the section's aria-labelledby. */}
      <div className="admin-band-bleed">
        <div className="admin-band">
          <Suspense fallback={null}>
            <Topography
              className="admin-band__topo"
              lowColor="#c7ced2"
              midColor="#c7ced2"
              highColor="#c7ced2"
              fadeCenter={[0.5, 0.5]}
              fadeInner={0}
              fadeOuter={1.5}
              fadeAspect={1.9}
              aspectCorrect={1}
              opacity={0.4}
              glow={0.3}
            />
          </Suspense>
          <div className="admin-band__cluster">
            <h1 id="admin-heading" className="admin-band__title">
              Admin Tools
            </h1>
            <p className="admin-band__subtitle">Governance, queues &amp; insight</p>
            {primaryRole && (
              <p className="admin-band__role">
                Signed in as <b>{primaryRole.label}</b>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 mb-7">
        <ProvisionalNote />
        <p className="max-w-[60ch] text-sm font-light text-text-inactive">
          Everything here belongs to the document that owns it — this page only indexes and links
          out (PC-F17). Sections below reflect your own live appointments.
        </p>
      </div>

      {unavailableReason !== null && (
        <p
          role="alert"
          className="mb-7 rounded-md border border-error px-field-x py-3 text-sm font-light text-error"
        >
          <span className="font-medium">The needs-attention counts are unavailable</span> —{' '}
          {unavailableReason}. The tiles below still work; they are simply showing no number, which
          is not the same as showing zero. Reload to try again.
        </p>
      )}

      {/*
        Two columns from the `sidebar` (1024px) breakpoint up: the sections on the left (~70%), a
        thin at-a-glance rail on the right (~30%). Below 1024px it is a single stacked column with the
        rail last. This is the whole point of the redesign — the page used to be one narrow column with
        the right half of a 1440px frame empty; splitting the destinations from the summary figures
        stops it reading as one long "smush of options and text". minmax(0,…) lets each column shrink
        rather than overflow on long hint text.
      */}
      <div className="grid grid-cols-1 gap-x-9 gap-y-9 sidebar:grid-cols-[minmax(0,7fr)_minmax(0,3fr)]">
        <div>
          {/* ── Queues (open in place over the grid, like Governance — Mauro 2026-08-27) ── */}
          {(isReviewer || isChampion || isSwat || isOwner) && (
            <>
              <h2 className="mb-4 text-base font-medium text-text">Queues</h2>
              <motion.div variants={staggerContainer(0.05)} initial="hidden" animate="visible">
                <BentoGrid className={SECTION_GRID}>
                  {(isReviewer || isOwner) && (
                    <TileShell
                      as="button"
                      triggerRef={triggerRefs.review}
                      onOpen={() => setOpenPanel('review')}
                      icon={ICONS.review}
                      label="Review queue"
                      hint="Pending tool submissions awaiting a decision."
                      count={counts?.pendingSubmissions}
                    />
                  )}
                  {(isChampion || isSwat || isOwner) && (
                    <TileShell
                      as="button"
                      triggerRef={triggerRefs.moderation}
                      onOpen={() => setOpenPanel('moderation')}
                      icon={ICONS.moderation}
                      label="Moderation queue"
                      hint="Flagged tool comments and forum posts awaiting a decision."
                      count={counts?.flaggedContent}
                    />
                  )}
                  {(isSwat || isOwner) && (
                    <TileShell
                      as="button"
                      triggerRef={triggerRefs.support}
                      onOpen={() => setOpenPanel('support')}
                      icon={ICONS.support}
                      label="Support & issues"
                      hint="Open issue reports and support requests."
                      count={counts?.unresolvedSupportRequests}
                    />
                  )}
                </BentoGrid>
              </motion.div>
            </>
          )}

          {/* ── Insight (read-only report — opens in place over the grid) ── */}
          {(isManager || isOwner) && (
            <>
              <h2 className="mt-9 mb-4 text-base font-medium text-text">Insight</h2>
              <motion.div variants={staggerContainer(0.05)} initial="hidden" animate="visible">
                <BentoGrid className={SECTION_GRID}>
                  <TileShell
                    as="button"
                    triggerRef={triggerRefs.metrics}
                    onOpen={() => setOpenPanel('metrics')}
                    icon={ICONS.metrics}
                    label="Learning metrics"
                    hint="Which learning content people open — aggregate only, names nobody."
                  />
                </BentoGrid>
              </motion.div>
            </>
          )}

          {!isReviewer && !isChampion && !isSwat && !isManager && !isOwner && (
            <p className="my-6 text-base font-light italic text-text-inactive">
              None of your current appointments unlock a queue here. If you believe this is wrong,
              ask the platform owner.
            </p>
          )}

          {/* ── Governance (the six panels — each opens IN PLACE over this grid) ── */}
          {isOwner && (
            <>
              <h2 className="mt-9 mb-4 text-base font-medium text-text">Governance</h2>
              <motion.div variants={staggerContainer(0.05)} initial="hidden" animate="visible">
                <BentoGrid className={SECTION_GRID}>
                  <TileShell
                    as="button"
                    triggerRef={triggerRefs.appointments}
                    onOpen={() => setOpenPanel('appointments')}
                    icon={ICONS.appointments}
                    label="Appointments"
                    hint="Grant or revoke a Reviewer/Champion/SWAT/Manager/Owner appointment."
                  />
                  <TileShell
                    as="button"
                    triggerRef={triggerRefs.teams}
                    onOpen={() => setOpenPanel('teams')}
                    icon={ICONS.teams}
                    label="Teams"
                    hint="Group people under a manager, with one or two champions — the manager and champions must already hold the appointment."
                  />
                  <TileShell
                    as="button"
                    triggerRef={triggerRefs.tools}
                    onOpen={() => setOpenPanel('tools')}
                    icon={ICONS.tools}
                    label="Tool Governance"
                    hint="Broken or ownerless tools, and the categories builders choose from."
                  />
                  <TileShell
                    as="button"
                    triggerRef={triggerRefs.access}
                    onOpen={() => setOpenPanel('access')}
                    icon={ICONS.access}
                    label="Access Requirements"
                    hint="Who may see a shared tool — the values builders choose from when they upload."
                  />
                  <TileShell
                    as="button"
                    triggerRef={triggerRefs.settings}
                    onOpen={() => setOpenPanel('settings')}
                    icon={ICONS.settings}
                    label="Platform Settings"
                    hint="Tune any product parameter with no deploy."
                  />
                  <TileShell
                    as="button"
                    triggerRef={triggerRefs.headcount}
                    onOpen={() => setOpenPanel('headcount')}
                    icon={ICONS.headcount}
                    label="Headcount"
                    hint="How many people the platform is for — the workforce the dashboard measures reach against."
                  />
                  <TileShell
                    as="button"
                    triggerRef={triggerRefs.terms}
                    onOpen={() => setOpenPanel('terms')}
                    icon={ICONS.terms}
                    label="Terms of Use"
                    hint="Edit and publish the usage policy; material changes re-gate everyone."
                  />
                </BentoGrid>
              </motion.div>
            </>
          )}
        </div>

        {/* ── Right rail: the summary figures, kept out of the tile grid so the destinations read as
            destinations and the numbers as numbers. Only rendered when it would carry something. ── */}
        {(isOwner || isReviewer || isChampion || isSwat) && (
          <aside aria-label="At a glance" className="flex flex-col gap-5">
            {/* At-a-glance: the same live counts as the tiles, as a compact list, each row opening the
                same panel via the same setOpenPanel handler. Gated per-queue exactly like the tiles.
                The rows do NOT claim the tiles' triggerRefs — useFocusTrap restores focus to the row
                itself on close, which is the correct landing spot for a click that started here. */}
            {(isReviewer || isChampion || isSwat || isOwner) && (
              <div className="rounded-md border border-subtle bg-surface p-card">
                <h2 className="mb-3 text-base font-medium text-text">At a glance</h2>
                <ul className="flex flex-col gap-2">
                  {(isReviewer || isOwner) && (
                    <GlanceRow
                      label="Review queue"
                      count={counts?.pendingSubmissions}
                      onOpen={() => setOpenPanel('review')}
                    />
                  )}
                  {(isChampion || isSwat || isOwner) && (
                    <GlanceRow
                      label="Moderation queue"
                      count={counts?.flaggedContent}
                      onOpen={() => setOpenPanel('moderation')}
                    />
                  )}
                  {(isSwat || isOwner) && (
                    <GlanceRow
                      label="Support & issues"
                      count={counts?.unresolvedSupportRequests}
                      onOpen={() => setOpenPanel('support')}
                    />
                  )}
                </ul>
              </div>
            )}

            {/* Needs attention (owner-only figures). The three-absence logic is preserved verbatim from
                the old mid-page block — only its container moved from a card grid into this rail card. */}
            {isOwner && (
              <div className="rounded-md border border-subtle bg-surface p-card">
                <h2 className="mb-3 text-base font-medium text-text">Needs attention</h2>
                <div className="flex flex-col gap-5">
                  <StatPlate label="Tools flagged Broken or needing an owner">
                    {/* The one count on this page an owner acts on, so the one it would be worst to
                        fake. `?? 0` used to print a reassuring zero for an outage, a 403 and a
                        genuinely empty platform alike; each now reads as itself. */}
                    {counts === null ? (
                      <span className="text-sm font-light text-text-inactive">
                        Not available — {unavailableReason ?? 'the summary could not be read'}.
                      </span>
                    ) : counts.brokenOrOwnerlessTools === undefined ? (
                      <span className="text-sm font-light text-text-inactive">
                        Not available — the summary did not carry this figure for your appointments.
                      </span>
                    ) : (
                      counts.brokenOrOwnerlessTools
                    )}
                  </StatPlate>
                  <StatPlate label="Neglected tools">
                    {/*
                      'unavailable' is rendered as words, never as 0 or a dash. View tracking started
                      with this deploy and there is no backfill, so for the first 90 days the platform
                      genuinely cannot tell an unvisited tool from an uncounted one — and an owner who
                      reads a dash as "none" would conclude the catalogue is healthy on missing data.

                      THREE ABSENCES, NOT TWO: `counts === null` is the unreadable summary, undefined is
                      the figure this viewer's appointments do not unlock, and 'unavailable' is the
                      window not yet covered — collapsing any two is the defect AdminSummaryState fixed.
                    */}
                    {counts === null ? (
                      <span className="text-sm font-light text-text-inactive">
                        Not available — {unavailableReason ?? 'the summary could not be read'}.
                      </span>
                    ) : counts.neglectedTools === undefined ? (
                      <span className="text-sm font-light text-text-inactive">
                        Not available — the summary did not carry this figure for your appointments.
                      </span>
                    ) : counts.neglectedTools === 'unavailable' ? (
                      <span className="text-sm font-light text-text-inactive">
                        Not available yet — view tracking has not been running long enough to tell
                        an unvisited tool from an uncounted one. This figure appears once the window
                        is covered.
                      </span>
                    ) : (
                      counts.neglectedTools
                    )}
                  </StatPlate>
                </div>
              </div>
            )}
          </aside>
        )}
      </div>

      {/* The open panel, mounted over the grid. Each fetches its own data on open. */}
      {openPanel === 'appointments' && (
        <AppointmentsPanel onClose={close} returnFocusRef={triggerRefs.appointments} />
      )}
      {openPanel === 'teams' && <TeamsPanel onClose={close} returnFocusRef={triggerRefs.teams} />}
      {openPanel === 'tools' && (
        <ToolGovernancePanel onClose={close} returnFocusRef={triggerRefs.tools} />
      )}
      {openPanel === 'access' && (
        <AccessRequirementsPanel onClose={close} returnFocusRef={triggerRefs.access} />
      )}
      {openPanel === 'settings' && (
        <PlatformSettingsPanel onClose={close} returnFocusRef={triggerRefs.settings} />
      )}
      {openPanel === 'headcount' && (
        <HeadcountPanel onClose={close} returnFocusRef={triggerRefs.headcount} />
      )}
      {openPanel === 'terms' && (
        <TermsOfUsePanel onClose={close} returnFocusRef={triggerRefs.terms} />
      )}
      {openPanel === 'review' && (
        <ReviewQueuePanel onClose={close} returnFocusRef={triggerRefs.review} />
      )}
      {openPanel === 'moderation' && (
        <ModerationQueuePanel onClose={close} returnFocusRef={triggerRefs.moderation} />
      )}
      {openPanel === 'support' && (
        <SupportQueuePanel onClose={close} returnFocusRef={triggerRefs.support} />
      )}
      {openPanel === 'metrics' && (
        <LearningMetricsPanel onClose={close} returnFocusRef={triggerRefs.metrics} />
      )}
    </section>
  );
}
